import React, { useState, createContext, useContext } from "react";
import {
  Box,
  Toolbar,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  Switch,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import PersonIcon from "@mui/icons-material/Person";
import LogoutIcon from "@mui/icons-material/Logout";
import Sidebar from "../components/Sidebar";
import { useThemeMode } from "../contexts/ThemeModeContext";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import Brightness7Icon from "@mui/icons-material/Brightness7";
import { useAppSelector, useAppDispatch } from "../store/hooks";
import { logout } from "../store/slices/authSlice";
import { clearProfile, fetchProfile } from "../store/slices/usersSlice";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ChangePasswordModal } from "../components/ChangePasswordModal";
import ImpersonationBanner from "../components/ImpersonationBanner";

const MainContent = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  paddingTop: 0,
  overflow: "auto",
  height: "100vh",
}));

interface SidebarContextType {
  isSidebarCollapsed: boolean;
  toggleSidebar: () => void;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (context === undefined) {
    throw new Error("useSidebar must be used within a SidebarProvider");
  }
  return context;
};

const MainLayout = ({ children }: { children: React.ReactNode }) => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [openChangePassword, setOpenChangePassword] = useState(false);

  const [profileMenuAnchor, setProfileMenuAnchor] =
    useState<null | HTMLElement>(null);
  const { mode, toggleTheme } = useThemeMode();
  const profile = useAppSelector((state) => state.users.profile);
  const profileLoading = useAppSelector((state) => state.users.profileLoading);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    if (!profile && !profileLoading) {
      dispatch(fetchProfile());
    }
  }, [profile, profileLoading, dispatch]);

  const handleSidebarToggle = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setProfileMenuAnchor(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setProfileMenuAnchor(null);
  };

  const handleProfileClick = () => {
    handleProfileMenuClose();
    navigate("/profile");
  };

  const handleSettingsClick = () => {
    handleProfileMenuClose();
    navigate("/settings");
  };

  const handleLogout = () => {
    handleProfileMenuClose();
    dispatch(logout());
    dispatch(clearProfile());
    navigate("/login");
  };

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <Sidebar isCollapsed={isSidebarCollapsed} onClick={handleSidebarToggle} />
      <Box sx={{ display: "flex", flexDirection: "column", flexGrow: 1 }}>
        <Box
          component="header"
          sx={{
            position: "absolute",
            margin: 0,
            width: "auto",
            borderRadius: 4,
            boxShadow: "none !important",
            right: 0,
            zIndex: 99,
          }}
        >
          <Toolbar
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <IconButton onClick={handleProfileMenuOpen} sx={{ ml: 2 }}>
                <Avatar
                  alt={
                    profile
                      ? `${profile.first_name} ${profile.last_name}`
                      : "User"
                  }
                  sx={{ width: 35, height: 35, fontSize: 14,background: "linear-gradient(135deg, #0052cc, #e57474)", }}
                >
                  {profile
                    ? getInitials(profile.first_name, profile.last_name)
                    : "U"}
                </Avatar>
              </IconButton>
              <Menu
                anchorEl={profileMenuAnchor}
                open={Boolean(profileMenuAnchor)}
                onClose={handleProfileMenuClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                PaperProps={{
                  sx: {
                    minWidth: 250,
                    mt: 1,
                  },
                }}
              >
                {profile && (
                  <>
                    <MenuItem
                      sx={{
                        flexDirection: "column",
                        alignItems: "flex-start",
                        py: 2,
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 1 }}
                      >
                        <Box>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {profile.first_name} {profile.last_name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {profile.email}
                          </Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                    <Divider />
                  </>
                )}
                <MenuItem sx={{ py: 1.5 }} onClick={handleProfileClick}>
                  <ListItemIcon>
                    <PersonIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Profile</ListItemText>
                </MenuItem>

                <MenuItem sx={{ py: 1.5 }} onClick={handleSettingsClick}>
                  <ListItemIcon>
                    <SettingsOutlinedIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Settings</ListItemText>
                </MenuItem>
                <MenuItem sx={{ py: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <ListItemIcon>
                      {mode === "dark" ? (
                        <Brightness7Icon fontSize="small" />
                      ) : (
                        <Brightness4Icon fontSize="small" />
                      )}
                    </ListItemIcon>
                    <ListItemText>
                      {mode === "dark" ? "Dark" : "Light"} Theme
                    </ListItemText>
                    <Switch
                      checked={mode === "dark"}
                      onChange={toggleTheme}
                      size="medium"
                      sx={{ ml: 1 }}
                    />
                  </Box>
                </MenuItem>
                <Divider />
                <MenuItem sx={{ py: 1.5 }} onClick={handleLogout}>
                  <ListItemIcon>
                    <LogoutIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Logout</ListItemText>
                </MenuItem>
              </Menu>
            </Box>
          </Toolbar>
        </Box>
        <MainContent>
          <ImpersonationBanner />
          <SidebarContext.Provider
            value={{ isSidebarCollapsed, toggleSidebar: handleSidebarToggle }}
          >
            {children}
          </SidebarContext.Provider>
        </MainContent>
      </Box>
      <ChangePasswordModal
        open={openChangePassword}
        onClose={() => setOpenChangePassword(false)}
        user={null}
      />
    </Box>
  );
};

export default MainLayout;
